import type { IconName } from '@risksmart-app/atomic-ui';

export const ICON_BUTTON_SIZE = 'size-8';

/* -------------------------------------------------------------------------- */
/*  Action descriptors                                                        */
/* -------------------------------------------------------------------------- */

export type ToolbarActionVariant = 'normal' | 'destructive';

/**
 * A single object action. The toolbar is presentational — the consuming HOC
 * resolves permissions, attaches handlers, and groups descriptors before
 * passing them in. The toolbar never sees a denied action.
 *
 * The same descriptor shape is reused across both destinations (primary inline
 * buttons and the overflow ⋮ menu) — see `ToolbarActions`.
 */
export interface ToolbarAction {
  id: string;
  label: string;
  iconName?: IconName;
  onClick: () => void;
  disabled?: boolean;
  variant?: ToolbarActionVariant;
}

/**
 * A cluster of related actions. Groups render in order inside the overflow ⋮
 * menu, each separated from the previous by a divider; an optional `label`
 * renders as a section heading above the group's items.
 *
 * The flat (ungrouped) case is just a single group — e.g.
 * `overflow={{ groups: [{ id: 'main', actions: [...] }] }}`. Reach for multiple
 * groups when actions split into meaningful clusters, e.g. a "form" group (Add
 * custom field, Edit form).
 */
export interface ToolbarActionGroup {
  id: string;
  /** Optional section heading rendered above the group's items. */
  label?: string;
  actions: ToolbarAction[];
}

/** Section label the toolbar applies to the synthesised `download` group. */
export const OVERFLOW_DOWNLOAD_LABEL = 'Download';
/** Section label the toolbar applies to the synthesised `danger` group. */
export const OVERFLOW_DANGER_LABEL = 'Danger zone';

/**
 * The contents of the overflow (⋮) menu. Three slots, all optional. The toolbar
 * — not the caller — owns the label and placement of the trailing `download` and
 * `danger` groups, so their position is stable across every object.
 *
 * Render order, top → bottom:
 *   1. `groups` — arbitrary caller-defined clusters, in order. Divider-separated;
 *      a group `label` renders as a section heading.
 *   2. `download` — auto-labelled "Download", always placed **second-last**.
 *   3. `danger` — auto-labelled "Danger zone", always placed **last**. Items are
 *      forced to the `destructive` variant.
 *
 * Pass `download` / `danger` as raw action arrays — the toolbar synthesises the
 * labelled groups. Omit any slot the object doesn't need.
 */
export interface ToolbarOverflow {
  groups?: ToolbarActionGroup[];
  download?: ToolbarAction[];
  danger?: ToolbarAction[];
}

/**
 * The toolbar's relocated actions, split by destination. Every slot is
 * optional; pass only the destinations an object needs.
 *
 * - `primary` — inline icon buttons at the left of the tray, in order. Use for
 *   the object's most-used actions (descriptor `label` becomes the button's
 *   accessible name).
 * - `overflow` — everything behind the ellipsis (⋮) menu, including download and
 *   destructive actions. See `ToolbarOverflow`.
 */
export interface ToolbarActions {
  primary?: ToolbarAction[];
  overflow?: ToolbarOverflow;
}

/* -------------------------------------------------------------------------- */
/*  Object header                                                             */
/* -------------------------------------------------------------------------- */

export interface ObjectToolbarHeader {
  /** The object's title, rendered at the left of the header. */
  title: string;
  /** Optional secondary count rendered beside the title. */
  counter?: string;
}

/* -------------------------------------------------------------------------- */
/*  Save / cancel controls                                                    */
/* -------------------------------------------------------------------------- */

export interface ObjectToolbarSave {
  onSave: () => void;
  /** true → save disc rendered greyed/disabled. */
  disabled?: boolean;
  loading?: boolean;
  /**
   * true → the object has unsaved changes, so the save disc oscillates between
   * the standard grey and teal to draw attention. Ignored when `disabled` or `loading`.
   */
  dirty?: boolean;
}

export type ObjectToolbarCancelMode = 'cancel' | 'approve-cancel';

export interface ObjectToolbarCancel {
  mode: ObjectToolbarCancelMode;
  /**
   * Required when `mode === 'approve-cancel'` (TPP > Response page). The X
   * button always navigates back (-1) — no onCancel needed.
   */
  onApprove?: () => void;
}

/* -------------------------------------------------------------------------- */
/*  Component props                                                           */
/* -------------------------------------------------------------------------- */

export interface ObjectToolbarProps {
  /** Object header — title, rendered left of the tray. */
  header: ObjectToolbarHeader;
  /**
   * Save control. `null`/omitted → no save disc. Must be `null` when
   * `cancel.mode === 'approve-cancel'` — the TPP > Response page shows an
   * Approve tick in place of Save.
   */
  save?: ObjectToolbarSave | null;
  /**
   * Cancel / dismiss controls. The X button only renders when `save` is also
   * present (or `mode === 'approve-cancel'`). Omit for read-only views.
   * The X button always calls `navigate(-1)` — no `onCancel` callback needed.
   */
  cancel?: ObjectToolbarCancel;
  /**
   * Relocated actions, split by destination (primary inline buttons and the
   * overflow ⋮ menu). Omit, or omit individual slots, for none. An empty /
   * all-empty overflow renders no ⋮ trigger.
   */
  actions?: ToolbarActions;
}
